import React from 'react';
import {
  CheckCircle, BookOpen, TrendingUp, BarChart2,
  Target, Brain, ScanLine, Shield, Activity, FlaskConical,
  Zap, PieChart, Layers, Star, Award, Compass, Play,
  ChevronRight, Map, ListChecks,
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { LEVELS_DATA_FINAL } from './LEVELS_DATA_FINAL';

export const PHASES = [
  { label: 'Foundation',   color: '#6366F1', range: [1,  4],  desc: 'Core concepts every trader must know'   },
  { label: 'Markets',      color: '#06b6d4', range: [5,  8],  desc: 'Reading markets, regimes, and setups'  },
  { label: 'Trading',      color: '#f59e0b', range: [9,  13], desc: 'Entries, exits, and trade management'  },
  { label: 'Review',       color: '#f97316', range: [14, 16], desc: 'Learning from your own numbers'        },
  { label: 'F&O Trading',  color: '#8b5cf6', range: [17, 18], desc: 'Futures & Options — fundamentals and risk management' },
];

const LEVEL_ICONS = [
  BookOpen, TrendingUp, BarChart2, Target,
  Brain, ScanLine, Shield, Activity,
  Target, Layers, PieChart, Zap,
  Compass, BarChart2, Brain, Star,
  FlaskConical, Award,
];

// ─── Circular progress ring ───────────────────────────────────────────────────
function Ring({ pct, color, size = 32 }) {
  const stroke = 2.5;
  const r      = (size - stroke * 2) / 2;
  const circ   = 2 * Math.PI * r;
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)', display: 'block' }}>
        <circle cx={size/2} cy={size/2} r={r} strokeWidth={stroke} fill="none" stroke={`${color}25`} />
        <circle cx={size/2} cy={size/2} r={r} strokeWidth={stroke} fill="none"
          stroke={color}
          strokeDasharray={`${(pct / 100) * circ} ${circ}`}
          strokeLinecap="round"
          style={{ transition: 'stroke-dasharray 0.6s ease' }}
        />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontSize: 7, fontWeight: 900, color, lineHeight: 1 }}>{pct}%</span>
      </div>
    </div>
  );
}

// ─── Resume card ─────────────────────────────────────────────────────────────
function ResumeCard({ level, getPhase, getPct, onSelect }) {
  const phase = getPhase(level.id);
  const pct   = getPct(level);
  const Icon  = LEVEL_ICONS[(level.id - 1) % LEVEL_ICONS.length];
  return (
    <div style={{ padding: '0 16px 8px' }}>
      <button
        onClick={() => onSelect(level.id)}
        onMouseEnter={e => e.currentTarget.style.borderColor = `${phase.color}70`}
        onMouseLeave={e => e.currentTarget.style.borderColor = `${phase.color}35`}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 14,
          padding: '16px', borderRadius: 16, cursor: 'pointer', textAlign: 'left',
          background: `linear-gradient(135deg, ${phase.color}18 0%, ${phase.color}08 100%)`,
          border: `1px solid ${phase.color}35`,
          boxShadow: `0 4px 24px ${phase.color}12`,
          transition: 'border-color 0.18s ease',
        }}
      >
        <div style={{ width: 44, height: 44, borderRadius: 12, flexShrink: 0, background: `${phase.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={20} color={phase.color} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: 10, color: phase.color, fontWeight: 700, marginBottom: 3, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            Level {level.id} · {phase.label}
          </p>
          <p style={{ fontSize: 15, fontWeight: 800, color: '#eceef5', letterSpacing: '-0.01em', lineHeight: 1.2, marginBottom: 8 }}>
            {level.name}
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ flex: 1, height: 3, borderRadius: 2, background: 'rgba(255,255,255,0.1)', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${Math.max(pct > 0 ? 4 : 0, pct)}%`, background: phase.color, borderRadius: 2, transition: 'width 0.5s ease' }} />
            </div>
            <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', flexShrink: 0 }}>{pct}%</span>
          </div>
        </div>
        <div style={{ width: 30, height: 30, borderRadius: 8, flexShrink: 0, background: `${phase.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Play size={13} color={phase.color} fill={phase.color} />
        </div>
      </button>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function LevelMap({ levels = [], currentLevel, onSelectLevel, onNavigate }) {
  const { colors } = useTheme();

  const levelDefMap = React.useMemo(() =>
    Object.fromEntries(LEVELS_DATA_FINAL.map(l => [l.id, l])), []);

  const sorted = React.useMemo(() =>
    [...levels].sort((a, b) => a.id - b.id), [levels]);

  const getPhase   = (id) => PHASES.find(p => id >= p.range[0] && id <= p.range[1]) ?? PHASES[0];
  const getPct     = (l)  => l.cards_total > 0 ? Math.round((l.cards_viewed / l.cards_total) * 100) : 0;
  const isComplete = (l)  => l.status === 'completed' || getPct(l) === 100;
  const isStarted  = (l)  => l.cards_viewed > 0;

  const resumeLevel = sorted.find(l => isStarted(l) && !isComplete(l))
    ?? sorted.find(l => !isComplete(l));

  const totalLevels = sorted.length;
  const doneLevels  = sorted.filter(isComplete).length;
  const totalCards  = sorted.reduce((s, l) => s + (l.cards_total || 0), 0);
  const viewedCards = sorted.reduce((s, l) => s + (l.cards_viewed || 0), 0);
  const overallPct  = totalCards > 0 ? Math.round((viewedCards / totalCards) * 100) : 0;

  if (!levels.length) {
    return <div style={{ padding: 24, color: colors.secondaryText, fontSize: 13 }}>Loading...</div>;
  }

  return (
    <div style={{ paddingBottom: 40 }}>

      {/* ── 1. HERO ── */}
      <div style={{ margin: '8px 16px 20px', borderRadius: 20, overflow: 'hidden', position: 'relative' }}>
        {/* Gradient bg */}
        <div style={{
          padding: '22px 18px 20px',
          background: 'linear-gradient(135deg, #6366F120 0%, #06b6d412 50%, #8b5cf608 100%)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 20,
        }}>
          <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#6366F1', marginBottom: 8 }}>
            Your Progress
          </p>
          <h1 style={{ fontSize: 26, fontWeight: 900, color: '#eceef5', letterSpacing: '-0.03em', lineHeight: 1.1, marginBottom: 6 }}>
            Build Your Edge
          </h1>
          <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', lineHeight: 1.5, marginBottom: 18 }}>
            Everything a trader must know — nothing more, nothing less.
          </p>

          {/* Overall progress bar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ flex: 1, height: 5, borderRadius: 3, background: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
              <div style={{
                height: '100%',
                width: `${Math.max(overallPct > 0 ? 2 : 0, overallPct)}%`,
                borderRadius: 3,
                background: 'linear-gradient(90deg, #6366F1, #06b6d4)',
                transition: 'width 0.6s ease',
              }} />
            </div>
            <span style={{ fontSize: 12, fontWeight: 800, color: '#6366F1', flexShrink: 0 }}>{overallPct}%</span>
          </div>
        </div>
      </div>

      {/* ── 2. METRICS ── */}
      <div style={{ display: 'flex', gap: 10, padding: '0 16px 20px' }}>
        {[
          { val: `${doneLevels}/${totalLevels}`, label: 'Levels done', color: '#6366F1' },
          { val: `${viewedCards}`,               label: 'Cards read',  color: '#06b6d4' },
          { val: `${overallPct}%`,               label: 'Complete',    color: '#f59e0b' },
        ].map(s => (
          <div key={s.label} style={{
            flex: 1, borderRadius: 14, padding: '12px 8px', textAlign: 'center',
            background: `linear-gradient(135deg, ${s.color}15 0%, ${s.color}05 100%)`,
            border: `1px solid ${s.color}28`,
          }}>
            <p style={{ fontSize: 20, fontWeight: 900, color: s.color, letterSpacing: '-0.03em', lineHeight: 1 }}>{s.val}</p>
            <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.38)', marginTop: 5, fontWeight: 500 }}>{s.label}</p>
          </div>
        ))}
      </div>

      {/* ── 3. FEATURE TILES (Trade Planning + Walkthrough) ── */}
      <div style={{ padding: '0 16px 24px' }}>
        {/* Section heading */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(255,255,255,0.35)', whiteSpace: 'nowrap' }}>
            Quick Access
          </span>
          <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.08)' }} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {/* Trade Planning tile */}
          <button
            onClick={() => onNavigate?.('/trade-planning')}
            onMouseEnter={e => e.currentTarget.style.borderColor = '#f59e0b55'}
            onMouseLeave={e => e.currentTarget.style.borderColor = '#f59e0b25'}
            style={{
              display: 'flex', flexDirection: 'column', textAlign: 'left',
              padding: '14px 12px', borderRadius: 16, cursor: 'pointer',
              background: 'linear-gradient(135deg, #f59e0b15 0%, #f59e0b05 100%)',
              border: '1px solid #f59e0b25',
              transition: 'border-color 0.18s ease',
            }}
          >
            <div style={{ width: 36, height: 36, borderRadius: 10, background: '#f59e0b18', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
              <Target size={16} color="#f59e0b" />
            </div>
            <p style={{ fontSize: 13, fontWeight: 800, color: '#eceef5', marginBottom: 3, lineHeight: 1.2 }}>Trade Planning</p>
            <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', lineHeight: 1.4, marginBottom: 12, flex: 1 }}>
              Decision engine + pre-trade checklist
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: '#f59e0b' }}>Open</span>
              <ChevronRight size={12} color="#f59e0b" />
            </div>
          </button>

          {/* Walkthrough tile */}
          <button
            onClick={() => onNavigate?.('/trade-walkthrough')}
            onMouseEnter={e => e.currentTarget.style.borderColor = '#06b6d455'}
            onMouseLeave={e => e.currentTarget.style.borderColor = '#06b6d425'}
            style={{
              display: 'flex', flexDirection: 'column', textAlign: 'left',
              padding: '14px 12px', borderRadius: 16, cursor: 'pointer',
              background: 'linear-gradient(135deg, #06b6d415 0%, #06b6d405 100%)',
              border: '1px solid #06b6d425',
              transition: 'border-color 0.18s ease',
            }}
          >
            <div style={{ width: 36, height: 36, borderRadius: 10, background: '#06b6d418', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
              <ListChecks size={16} color="#06b6d4" />
            </div>
            <p style={{ fontSize: 13, fontWeight: 800, color: '#eceef5', marginBottom: 3, lineHeight: 1.2 }}>Trade Walkthrough</p>
            <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', lineHeight: 1.4, marginBottom: 12, flex: 1 }}>
              6-step framework for every trade
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: '#06b6d4' }}>Open</span>
              <ChevronRight size={12} color="#06b6d4" />
            </div>
          </button>
        </div>
      </div>

      {/* ── Connector hint ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '0 16px 20px' }}>
        <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.07)' }} />
        <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', fontWeight: 500, textAlign: 'center', lineHeight: 1.5, maxWidth: 220 }}>
          Complete the levels below, then use the links above to plan your trades
        </p>
        <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.07)' }} />
      </div>

      {/* ── 4. RESUME CARD ── */}
      {resumeLevel && (
        <div style={{ paddingBottom: 16 }}>
          {/* Section heading */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 16px', marginBottom: 12 }}>
            <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(255,255,255,0.35)', whiteSpace: 'nowrap' }}>
              Continue
            </span>
            <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.08)' }} />
          </div>
          <ResumeCard level={resumeLevel} getPhase={getPhase} getPct={getPct} onSelect={onSelectLevel} />
        </div>
      )}

      {/* ── 5. PHASE SECTIONS ── */}
      {PHASES.map(phase => {
        const phaseLevels = sorted.filter(l => l.id >= phase.range[0] && l.id <= phase.range[1]);
        if (!phaseLevels.length) return null;
        const phaseDone = phaseLevels.filter(isComplete).length;

        return (
          <div key={phase.label} style={{ marginBottom: 28, padding: '0 16px' }}>

            {/* Discover-style section heading */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: phase.color, whiteSpace: 'nowrap' }}>
                {phase.label}
              </span>
              <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.08)' }} />
              <span style={{
                fontSize: 10, fontWeight: 700, color: phase.color,
                background: `${phase.color}15`, border: `1px solid ${phase.color}30`,
                padding: '2px 8px', borderRadius: 8,
              }}>
                {phaseDone}/{phaseLevels.length}
              </span>
            </div>

            {/* 2-col tile grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {phaseLevels.map(level => {
                const pct     = getPct(level);
                const done    = isComplete(level);
                const started = isStarted(level) && !done;
                const Icon    = LEVEL_ICONS[(level.id - 1) % LEVEL_ICONS.length];

                const bgStart = done ? `${phase.color}22` : started ? `${phase.color}18` : `${phase.color}12`;
                const bgEnd   = done ? `${phase.color}10` : started ? `${phase.color}08` : `${phase.color}04`;
                const borderC = done ? `${phase.color}50` : started ? `${phase.color}40` : `${phase.color}22`;

                return (
                  <button
                    key={level.id}
                    onClick={() => onSelectLevel(level.id)}
                    onMouseEnter={e => e.currentTarget.style.borderColor = `${phase.color}65`}
                    onMouseLeave={e => e.currentTarget.style.borderColor = borderC}
                    style={{
                      display: 'flex', flexDirection: 'column', textAlign: 'left',
                      padding: '12px 11px', borderRadius: 14, cursor: 'pointer',
                      background: `linear-gradient(135deg, ${bgStart} 0%, ${bgEnd} 100%)`,
                      border: `1px solid ${borderC}`,
                      transition: 'border-color 0.18s ease',
                      minHeight: 110,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10 }}>
                      <div style={{ width: 34, height: 34, borderRadius: 10, background: `${phase.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Icon size={15} color={phase.color} />
                      </div>
                      {done
                        ? <CheckCircle size={15} color={phase.color} />
                        : started
                        ? <Ring pct={pct} color={phase.color} size={32} />
                        : <span style={{ fontSize: 9, color: `${phase.color}70`, fontWeight: 700, marginTop: 2 }}>
                            L.{String(level.id).padStart(2, '0')}
                          </span>
                      }
                    </div>

                    <p style={{
                      fontSize: 12, fontWeight: 700, lineHeight: 1.35, flex: 1,
                      color: done || started ? '#eceef5' : 'rgba(255,255,255,0.5)',
                      marginBottom: 8,
                    }}>
                      {level.name}
                    </p>

                    {done ? (
                      <span style={{ fontSize: 9, color: phase.color, fontWeight: 700 }}>✓ Complete</span>
                    ) : started ? (
                      <div>
                        <div style={{ height: 2, borderRadius: 1, background: 'rgba(255,255,255,0.08)', overflow: 'hidden', marginBottom: 4 }}>
                          <div style={{ height: '100%', width: `${pct}%`, background: phase.color, borderRadius: 1 }} />
                        </div>
                        <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)' }}>
                          {level.cards_viewed} / {level.cards_total}
                        </span>
                      </div>
                    ) : (
                      <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.25)' }}>
                        {level.cards_total} cards
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
